import { useQueryClient } from "@tanstack/react-query";
import { useId, type SubmitEvent } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import { useLogin } from "@/api/bale";
import { isOrvalError } from "@/compat";
import { Button } from "@/components/Button";
import { Checkbox } from "@/components/Checkbox";
import { Form } from "@/components/Form";
import { Input } from "@/components/Input";

export function LoginForm({ onForgotPassword }: { onForgotPassword: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const search = new URLSearchParams(location.search);
  const login = useLogin({
    mutation: {
      onSuccess: async () => {
        await queryClient.resetQueries();
        await navigate(search.get("next") ?? "/overview");
      },
    },
  });

  async function handleSubmit(e: SubmitEvent<HTMLFormElement>) {
    const passwordField = e.currentTarget.elements.namedItem("password") as HTMLInputElement;

    const form = new FormData(e.currentTarget);
    const id = form.get("username")! as string;
    const password = form.get("password")! as string;
    const remember_me = form.has("remember_me");
    try {
      await login.mutateAsync({ data: { id, password, remember_me } });
    } catch (error) {
      if (isOrvalError(error) && error.info.code === "CredentialsIncorrect") {
        passwordField.setCustomValidity(t("password-is-incorrect"));
        passwordField.reportValidity();
      }
    }
  }
  const labelId = useId();

  return (
    <div className="flex flex-col items-center">
      <h2 className="mb-6 text-center font-title" id={labelId}>
        {t("login-title")}
      </h2>
      <Form
        error={login.error}
        onSubmit={handleSubmit}
        className="flex w-full flex-col gap-6"
        aria-labelledby={labelId}
      >
        <Input name="username" type="text" required placeholder={t("username")} disabled={login.isPending} />
        <Input type="password" name="password" required placeholder={t("password")} disabled={login.isPending} />
        <label className="flex items-center gap-4">
          <Checkbox name="remember_me" disabled={login.isPending} />
          <p className="font-interest">{t("remember-me")}</p>
        </label>
        <Button type="submit" disabled={login.isPending}>
          {t("login-action")}
        </Button>
      </Form>
      <button type="button" className="mt-2 cursor-pointer font-hint" onClick={onForgotPassword}>
        {t("login-forgot-password")}
      </button>
    </div>
  );
}
